var $ = require('jquery');
var _ = require('underscore');
var ModalDialogWidget = require('./../widget/modal/ModalDialogWidget');
var AppointmentsOnDayListWidget = require('./AppointmentsOnDayListWidget');
var SecureMessageAppointmentSimpleView = require('./SecureMessageAppointmentSimpleView');
var wizardUtils = require('./../wizard/wizardUtils');
var launcherAppInterface = require('./../launcherApplicationInterface');

/**
 * Modal that allows the user to select whether to register multiple appointments on the same day.
 * @class MultiAppointmentSelectionModalView
 * @constructor
 */
module.exports = ModalDialogWidget.extend({
  className: 'brec-dialog brec-dialog--regular',

  /**
   * @method initialize
   * @override
   * @param options {Object} the options of this view.
   *   @param options.userIsStaff {Boolean} true if the user is a staff user, false otherwise.
   *   @param options.patientVecnaId {String} the id of the patient to display the appts for.
   *   @param options.appointmentExternalId {String} the external id of the appointment to display.
   *   @param options.regDateMoment {Moment} the date to display all appointments for as a moment date object.
   // Inherited from ModalDialogWidget
   *   @param [options.dataViewName] {String} name for the data view
   *   @param [options.dataViewTestIdentifier] {String} unique identifier suffix to use for data-test attribute.
   *                                                    Attribute will be "data-test-modal-data-{{dataViewTestIdentifier}}".
   *   @param [options.headerKey=_defaultHeaderKey] {String} i18n key for the header
   *   @param [options.descriptionKey=null] {String} i18n key for the description
   *   @param [options.description=null] {String} text for the description
   *   @param [options.bottomDescriptionKey=null] {String} i18n key for the bottom description (when trackedAboveDescription = false)
   *   @param [options.bottomDescription=null] {String} text for the bottom description (when trackedAboveDescription = false)
   *   @param [options.yesKey=_defaultYesKey] {String} i18n key for the 'yes' button
   *   @param [options.yesClass=null] {String} additional classes for the 'yes' button
   *   @param [options.noKey=_defaultNoKey] {String} i18n key for the 'no' button
   *   @param [options.closeKey=_defaultCloseKey] {String} i18n key for the 'close' button
   *   @param [options.closeTitleKey=_defaultCloseTitleKey] {String} i18n key for the 'x' button's mouseover
   *   @param [options.alertOnly=false] {boolean} if true, displays close button only
   *   @param [options.hideClose=false] {boolean} if true, does not display the 'x' button
   *   @param [options.stackButtons=false] {boolean} if true, vertically stacks the 'yes' and 'no' buttons
   *   @param [options.noPrimary=false] {boolean} if true, the 'yes' button will not be styled 'primary'
   *   @param [options.trackedView=null] {view} optional embedded child view instance
   *   @param [options.trackedViewOptions=null] {Object} attachment options for tracked view
   *   @param [options.trackedAboveDescription=false] {boolean} if true, put the embedded child view above the description
   *   @param [options.yesEnabled=false] {boolean} if true, the yes button is enabled.  False it is disabled.
   *   @param [options.noEnabled=false] {boolean} if true, the no button is enabled.  False it is disabled.
   *   @param [options.errorDescription=null] {string} the raw string to use for the error description (when yesEnabled = false).
   *   @param [options.errorDescriptionKey=null] {string} the message key to use for the error description (when yesEnabled = false).
   */
  initialize: function(options) {
    this._patientVecnaId = options.patientVecnaId;
    this._appointmentExternalId = options.appointmentExternalId;
    this._regDate = options.regDateMoment && options.regDateMoment.format(wizardUtils.APPOINTMENT_REG_DAY_FORMAT);
    this._userIsStaff = options.userIsStaff;
    options.trackedView = options.trackedView || new AppointmentsOnDayListWidget({
      className: '', // clear the class name to remove the extra padding around SecureListWidget.
      appointmentDate: options.regDateMoment,
      itemView: SecureMessageAppointmentSimpleView,
      itemContext: {
        className: 'brec-list__item brec-list__item--top-border'
      },
      selectable: true,
      defaultSelected: true,
      loadAllAppointments: true,
      headerMessageKey: false,
      userIsStaff: options.userIsStaff,
      patientVecnaId: this._patientVecnaId
    });
    options.dataViewName = options.dataViewName || 'multi-appointment modal';
    options.dataViewTestIdentifier = options.dataViewTestIdentifier || 'data-test-multi-appointment-modal';
    var checkinRegisterKeySuffix = launcherAppInterface.isAtHospital() ? '.checkin' : '.register';
    var defaultHeaderKey = 'multi.appointment.modal.header' + checkinRegisterKeySuffix;
    options.headerKey = options.headerKey || defaultHeaderKey;
    var defaultMultipleResultsDescriptionKey = 'multi.appointment.modal.description' + checkinRegisterKeySuffix;
    this.set('multipleResultsDescriptionKey', defaultMultipleResultsDescriptionKey);
    var defaultYesKeyMessageKey = 'multi.appointment.modal.selected' + checkinRegisterKeySuffix;
    options.yesKey = options.yesKey || defaultYesKeyMessageKey;
    options.noKey = options.noKey || 'multi.appointment.modal.cancel';
    options.errorDescriptionKey = 'multi.appointment.modal.selected.error' + checkinRegisterKeySuffix;

    ModalDialogWidget.prototype.initialize.apply(this, arguments);

    // When the tracked view completes a fetch, then re-render this view to pick up the number of appointments.
    this.listenTo(this._trackedView, 'render:complete', this._navigateIfOnlyOneAppointment);
    this._navigateIfOnlyOneAppointment();

    this.listenTo(this._trackedView, 'render:complete itemView:change:selected', this._validateSelection);
    this._validateSelection();

    this.listenTo(this._trackedView, 'messagesFetched', this.render);
  },

  /**
   * @override
   * @method prerender
   */
  prerender: function() {
    var descriptionWithNumber = false;
    var appointmentListView = this._trackedView;
    if (appointmentListView.getNumberOfMessages() > 1) {
      var multipleResultsDescriptionKey = this.get('multipleResultsDescriptionKey');
      descriptionWithNumber = $.i18n.prop(multipleResultsDescriptionKey, appointmentListView.getNumberOfMessages());
    } else if (launcherAppInterface.isAtHospital()) {
      descriptionWithNumber = $.i18n.prop('multi.appointment.modal.description.checkin.noResults');
      appointmentListView.noDataMessageKey = $.i18n.prop('multi.appointment.modal.description.checkin.noMessages');
    }
    this.set('description', descriptionWithNumber);
  },

  /**
   * @method _attached
   * @override
   */
  _attached: function() {
    this._navigateIfOnlyOneAppointment();
  },

  /**
   * Navigate to the single url endpoint if there is only one appointment to pre-reg.
   * @method _navigateIfOnlyOneAppointment
   * @private
   */
  _navigateIfOnlyOneAppointment: function() {
    var appointmentListView = this._trackedView;
    if (appointmentListView.hasOnlyOneAppointmentToSelect()) {
      this.confirm();
    }
  },

  /**
   * Override confirm to instead redirect to the correct appointment reg url.
   * @method confirm
   * @private
   */
  confirm: function() {
    if (!this.get('yesEnabled')) {
      return;
    }

    var appointmentListView = this._trackedView;
    var selectedAppointmentViews = appointmentListView.getSelectedItemViews();
    var selectedAppointmentExternalIds = _.map(selectedAppointmentViews, function(secureMessageAppointmentSimpleView) {
      return secureMessageAppointmentSimpleView.getAppointmentExternalId();
    });
    wizardUtils.saveSelectedPreRegWizardAppointmentExternalIds(this._regDate, selectedAppointmentExternalIds);
    var regDateToUse = this._regDate;
    var selectedAppointmentExternalId = this._appointmentExternalId;
    this._validateSelection();
    if (selectedAppointmentExternalIds.length === 1) {
      regDateToUse = null;
      selectedAppointmentExternalId = selectedAppointmentExternalIds[0];
    }
    var rootRegUrl = wizardUtils.generateRootRegUrl({
      patientVecnaId: this._patientVecnaId,
      appointmentExternalId: selectedAppointmentExternalId,
      regDate: regDateToUse,
      userIsStaff: this._userIsStaff
    });

    this.resolveModal();
    window.location = rootRegUrl;
  },

  /**
   * If the selection is valid (1+) then enable the yes button.  Otherwise disable it.
   * @method _validateSelection
   * @private
   */
  _validateSelection: function() {
    var appointmentListView = this._trackedView;
    var selectedItems = appointmentListView.getSelectedItemViews();
    if (appointmentListView.isFetchSuccess()) {
      var selectionValid = (selectedItems && selectedItems.length > 0);
      this.set('yesEnabled', selectionValid);
    }
  }
});
